import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { StaffShell } from "@/components/layout/StaffShell";
import { adminNav } from "@/lib/nav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CenterSpinner, EmptyState } from "@/components/common/States";

export default function AdminCategories() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["categories"], queryFn: () => api("/categories") });
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");

  const create = async () => {
    try {
      await api("/categories", { method: "POST", json: { name } });
      toast.success("Category added");
      setOpen(false);
      setName("");
      qc.invalidateQueries({ queryKey: ["categories"] });
    } catch (e) {
      toast.error(e.message);
    }
  };

  const remove = async (c) => {
    try {
      await api(`/categories/${c.category_id}`, { method: "DELETE" });
      toast.success("Category removed");
      qc.invalidateQueries({ queryKey: ["categories"] });
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <StaffShell
      items={adminNav}
      roleLabel="Admin"
      title="Categories"
      headerRight={<Button className="rounded-full" onClick={() => setOpen(true)} data-testid="add-category-button"><Plus className="mr-1 h-4 w-4" /> Add</Button>}
    >
      {isLoading ? (
        <CenterSpinner />
      ) : !data?.length ? (
        <EmptyState title="No categories yet" action={<Button className="rounded-full" onClick={() => setOpen(true)}>Add category</Button>} />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((c) => (
            <Card key={c.category_id} className="flex items-center justify-between rounded-2xl p-4" data-testid={`category-${c.category_id}`}>
              <div>
                <p className="font-display text-base">{c.name}</p>
                <p className="text-xs text-muted-foreground">{c.vendor_count} vendor{c.vendor_count === 1 ? "" : "s"}</p>
              </div>
              <Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(c)} data-testid={`delete-category-${c.category_id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add category</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="category-name-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-full" onClick={create} disabled={!name} data-testid="save-category-button">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </StaffShell>
  );
}
